import { useMemo } from "react";

/**
 * useResourceSplit — derives `freePersons`, `busyPersons`, `onLeavePersons`, `freeCars`
 * from raw `data.persons` + `data.cars`. Pure memoized derivation.
 *
 * Rules:
 *  - Excludes admin users from free/busy/onLeave (admins don't appear in the daily availability).
 *  - A person counts as "onLeave" if they have ANY vacation entry this week (server already
 *    filters vacations to the current week, so `vacations.length > 0` is sufficient).
 *  - Cars: `is_free` flag from backend = not allocated this week + not defect (unless defect_drivable=true).
 *
 * @param {object} data — { persons: [{id, name, is_admin_user, is_free, vacations: [...]}], cars: [{id, label, is_free, status, defect_drivable}] }
 * @returns {{ freePersons, busyPersons, onLeavePersons, freeCars }}
 */
export default function useResourceSplit(data) {
  return useMemo(() => {
    const allPersons = data?.persons || [];
    const hasVac = (p) => (p?.vacations || []).length > 0;

    const freePersons = allPersons.filter((p) => p.is_free && !p.is_admin_user && !hasVac(p));
    const onLeavePersons = allPersons.filter((p) => hasVac(p));
    const busyPersons = allPersons.filter((p) => !p.is_free && !p.is_admin_user && !hasVac(p));
    const freeCars = (data?.cars || []).filter((c) => c.is_free);

    return { freePersons, busyPersons, onLeavePersons, freeCars };
  }, [data]);
}
